import type { IMessage } from "react-native-gifted-chat";

import type { ConversationMessage, UserThread } from "@/generated/graphql";
import { ConversationRole } from "@/generated/graphql";
import { formatDate } from "@/utils/intlFormat";

/**
 * Type guard to check if a node is a valid UserThread
 * Filters out null and undefined values from GraphQL responses
 * @param node - The node to check
 * @returns True if node is a valid UserThread
 */
export const isValidUserThread = (node: unknown): node is UserThread =>
  node !== null && node !== undefined;

/**
 * Transform GraphQL ConversationMessage to react-native-gifted-chat IMessage format
 * @param message - GraphQL ConversationMessage object containing message content and metadata
 * @returns IMessage object formatted for react-native-gifted-chat with user info and optional metadata
 */
export const transformConversationMessageToIMessage = (
  message: ConversationMessage
): IMessage => {
  const isUserMessage = message.role === ConversationRole.User;

  return {
    _id: message.id,
    text: message.content,
    createdAt: new Date(message.timestamp),
    user: {
      _id: isUserMessage ? "user" : "ai",
      name: isUserMessage ? "You" : "Assistant",
    },
    // Add metadata if available
    ...(message.metadata && {
      metadata: {
        model: message.metadata.model,
        inputTokens: message.metadata.inputTokens,
        outputTokens: message.metadata.outputTokens,
        dataSources: message.metadata.dataSources,
        additionalData: message.metadata.additionalData,
      },
    }),
  } as IMessage;
};

/**
 * Transform array of GraphQL ConversationMessages to IMessage array
 * Sorts messages by timestamp (newest first) to match GiftedChat display order
 * @param messages - Array of GraphQL ConversationMessage objects to transform
 * @returns Array of IMessage objects sorted newest-first for GiftedChat display
 */
export const transformConversationMessagesToIMessages = (
  messages: ConversationMessage[]
): IMessage[] => {
  return messages.map(transformConversationMessageToIMessage).sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateB - dateA; // Reverse order for GiftedChat (newest first)
  });
};

/**
 * Transform IMessage back to ConversationMessage format for API operations
 * Note: This is primarily used for local state management and may not include all backend fields
 * @param message - IMessage object from react-native-gifted-chat to transform
 * @returns Partial ConversationMessage with id, content, role, timestamp, and optional metadata
 */
export const transformIMessageToConversationMessage = (
  message: IMessage
): Partial<ConversationMessage> => {
  const messageWithMetadata = message as IMessage & {
    metadata?: {
      model?: string;
      inputTokens?: number;
      outputTokens?: number;
      dataSources?: string[];
      additionalData?: Record<string, unknown>;
    };
  };

  return {
    id: message._id.toString(),
    content: message.text || "",
    role:
      message.user._id === "user"
        ? ConversationRole.User
        : ConversationRole.Assistant,
    timestamp: message.createdAt.toString(),
    ...(messageWithMetadata.metadata && {
      metadata: {
        model: messageWithMetadata.metadata.model,
        inputTokens: messageWithMetadata.metadata.inputTokens,
        outputTokens: messageWithMetadata.metadata.outputTokens,
        dataSources: messageWithMetadata.metadata.dataSources,
        additionalData: messageWithMetadata.metadata.additionalData,
      },
    }),
  };
};

/**
 * Generate a thread title from the first message content
 * Truncates to 50 characters and removes line breaks
 * @param firstMessage - First message content to generate title from (optional)
 * @returns Cleaned title string truncated to 50 chars, or "New Conversation" if no message provided
 */
export const generateThreadTitle = (firstMessage?: string): string => {
  if (!firstMessage) {
    return "New Conversation";
  }

  const cleanedMessage = firstMessage
    .replace(/\n/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  return cleanedMessage.length > 50
    ? `${cleanedMessage.substring(0, 50)}...`
    : cleanedMessage;
};

/**
 * Format thread last accessed time for display
 * @param lastAccessed - ISO 8601 timestamp string of last access time
 * @param locale - The active UI locale, for the absolute-date fallback
 * @returns Human-readable relative time string (e.g., "Just now", "2h ago", "3d ago", "Jan 15")
 */
export const formatThreadLastAccessed = (
  lastAccessed: string,
  locale: string
): string => {
  const date = new Date(lastAccessed);
  const now = new Date();
  const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

  if (diffInHours < 1) {
    return "Just now";
  }

  if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  }

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) {
    return `${diffInDays}d ago`;
  }

  // For older dates, show formatted date.
  // SE-7966: an `undefined` locale resolves to the DEVICE locale; it now
  // follows the account's languagePreference. The year is still added only
  // when the date falls outside the current year — that conditional is about
  // relevance, not locale, and is unchanged.
  return formatDate(date, locale, {
    month: "short",
    day: "numeric",
    ...(date.getFullYear() !== now.getFullYear() && { year: "numeric" }),
  });
};

/**
 * Sort threads by last accessed time (most recent first)
 * @param threads - Array of thread objects with lastAccessed property to sort
 * @returns New array of threads sorted by lastAccessed descending (most recent first)
 */
export const sortThreadsByLastAccessed = <T extends { lastAccessed: string }>(
  threads: T[]
): T[] => {
  return [...threads].sort((a, b) => {
    const dateA = new Date(a.lastAccessed).getTime();
    const dateB = new Date(b.lastAccessed).getTime();
    return dateB - dateA;
  });
};

/**
 * Filter threads based on search query
 * Searches in title and firstQuestion fields
 * @param threads - Array of thread objects with title and firstQuestion properties
 * @param query - Search query string to filter threads by
 * @returns Filtered array of threads matching all search terms (case-insensitive)
 */
export const filterThreadsByQuery = <
  T extends { title?: string | null; firstQuestion?: string | null },
>(
  threads: T[],
  query: string
): T[] => {
  if (!query.trim()) {
    return threads;
  }

  const searchTerms = query.toLowerCase().split(" ").filter(Boolean);

  return threads.filter(thread => {
    const searchText =
      `${thread.title || ""} ${thread.firstQuestion || ""}`.toLowerCase();
    return searchTerms.every(term => searchText.includes(term));
  });
};

/**
 * OpenAI text content structure
 *
 * Represents a text content block within an OpenAI message
 */
interface OpenAITextContent {
  type: "text";
  text: {
    value: string | null;
  };
}

/**
 * OpenAI non-text content structure (images, files, etc.)
 *
 * Represents non-text content blocks that should be filtered out
 */
interface OpenAINonTextContent {
  type: string;
  [key: string]: unknown;
}

/**
 * Union type for all OpenAI content types
 */
type OpenAIContent = OpenAITextContent | OpenAINonTextContent;

/**
 * OpenAI message structure
 *
 * Represents a message from OpenAI's chat completion API with role and content array
 */
export interface OpenAIMessage {
  id?: string;
  role: "user" | "assistant";
  content: OpenAIContent[];
}

/**
 * Type guard to check if content is text content
 * @param content - Content block to check
 * @returns True if content is text type with a value
 */
const isTextContent = (
  content: OpenAIContent
): content is OpenAITextContent => {
  if (content.type !== "text") {
    return false;
  }
  // Type assertion is safe here because we've checked type === "text"
  const textContent = content as OpenAITextContent;
  return textContent.text?.value !== null;
};

/**
 * Transform OpenAI message to react-native-gifted-chat IMessage format
 *
 * Extracts text content from OpenAI message structure and converts to IMessage format.
 * Filters out non-text content types (images, files) and handles missing text values.
 * @param message - OpenAI message object with content array and role
 * @param index - Message index used as fallback ID if message.id not available
 * @returns IMessage formatted for react-native-gifted-chat component
 */
export const transformOpenAiMessageToIMessage = (
  message: OpenAIMessage,
  index: number
): IMessage => {
  // Extract text content from OpenAI message structure
  const textContent = message.content
    .filter(isTextContent)
    .map(content => content.text.value!)
    .join(" ");

  // Determine if this is a user or assistant message
  const isUserMessage = message.role === "user";

  return {
    _id: message.id || index.toString(),
    text: textContent,
    createdAt: new Date(), // OpenAI messages don't have timestamps, use current time
    user: {
      _id: isUserMessage ? "user" : "ai",
      name: isUserMessage ? "You" : "Assistant",
    },
  };
};

/**
 * Transform array of OpenAI messages to IMessage array
 *
 * Converts all messages to IMessage format and reverses the array to match
 * GiftedChat's display order (newest messages first).
 * @param messages - Array of OpenAI message objects to transform
 * @returns Array of IMessage objects in reverse order (newest first)
 */
export const transformOpenAiMessagesToIMessages = (
  messages: OpenAIMessage[]
): IMessage[] => {
  return messages
    .map((message, index) => transformOpenAiMessageToIMessage(message, index))
    .reverse(); // Reverse to show newest first for GiftedChat
};
